import React, { useEffect, useContext, useState, useRef } from 'react';
import { isAuthenticated, getAuthInfoForWebStorage, exchangeCodeForTokens, ACCOUNT_NOT_REGISTERED_MESSAGE_KEYWORD } from '../utils/awsCognito';
import { Box, Button, CircularProgress, Fade, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { WebAPIContext } from '../contexts/WebAPIContext';
import { Routes, Route } from 'react-router-dom';
import { ApplicationPathName } from '../contexts/constants';
import SignIn from './SignIn/SignIn';
import Faq from "./Faq/Faq";
import AppHeader from '../components/AppHeader';
import Menu from '../components/Menu';
import AppContent from '../components/AppContent';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useSelector } from "react-redux";
import { closeMessage, setMessage, type MessageType } from '../store/messageSlice';
import { useTranslation } from 'react-i18next';
import { setAuthInfo, signOut } from '../store/authSlice';
import { RootState } from "../store/store";
import './App.css';

// 画面遷移を監視するためにグローバルに定義しておく
// App内では画面遷移で初期化されてしまうため
let currentPath: string | null = null;
// 認証ログ出力済みフラグ
let isAuthLogSent = false;

const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin';

const App: React.FC = () => {
  const global = useSelector((state: RootState) => state.store);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isOpenModal, message, type, detail } = useAppSelector((state) => state.message);
  const { email, userName } = useAppSelector((state) => state.auth);
  const WebAPI = useContext(WebAPIContext);
  const { t } = useTranslation();

  // 認証情報が確定するまで保護コンテンツのレンダリングを止めるフラグ
  const [isAuthReady, setIsAuthReady] = useState(false);
  // React StrictMode での useEffect 二重実行による OAuth code 二重消費を防ぐフラグ
  const isInitStartedRef = useRef(false);

  useEffect(() => {
    if (isInitStartedRef.current) return;
    isInitStartedRef.current = true;

    const init = async () => {
      const params = new URLSearchParams(window.location.search);

      // Cognito OAuth コールバック（?code=&state=）の場合
      if (params.has('code') && params.has('state')) {
        try {
          const userInfo = await exchangeCodeForTokens(params.get('code')!, params.get('state')!);

          // システム利用可否チェック
          const checkResult = await WebAPI.request({
            query: `
              query getSystemAccessible($userId: String) {
                getSystemAccessible(userId: $userId) {
                  isSystemAccessible
                }
              }
            `,
            variables: { userId: userInfo.email }
          });
          if (!checkResult?.data?.getSystemAccessible?.isSystemAccessible) {
            await dispatch(signOut());
            dispatch(setMessage({ message: t('features.SignIn.message.noAccessRight'), type: 'error' as MessageType }));
            setIsAuthReady(true);
            navigate(ApplicationPathName.SignIn.Url, { replace: true });
            return;
          }
          dispatch(setAuthInfo({ email: userInfo.email, userId: userInfo.sub, userName: userInfo.username }));
          setIsAuthReady(true);
          const redirect = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
          sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
          const safePath =
            redirect?.startsWith('/') && !redirect.startsWith('//') && redirect !== ApplicationPathName.SignIn.Url
              ? redirect
              : '/';
          navigate(safePath, { replace: true });
        } catch (e) {
          console.error('OAuth callback failed:', e);
          await dispatch(signOut());
          dispatch(setMessage({ message: t('features.SignIn.message.oauthFailed'), type: 'error' as MessageType }));
          setIsAuthReady(true);
          navigate(ApplicationPathName.SignIn.Url, { replace: true });
        }
        return;
      }

      // Cognito OAuth エラーレスポンス（?error=...）の場合
      if (params.has('error')) {
        const error = params.get('error');
        const errorDescription = params.get('error_description') ?? '';
        const isAccountNotRegistered =
          error === 'invalid_request' &&
          (errorDescription.includes('PreSignUp') || errorDescription.includes(ACCOUNT_NOT_REGISTERED_MESSAGE_KEYWORD));
        await dispatch(signOut());
        dispatch(setMessage({
          message: isAccountNotRegistered
            ? t('features.SignIn.message.accountNotRegistered')
            : t('features.SignIn.message.oauthFailed'),
          type: 'error' as MessageType,
        }));
        setIsAuthReady(true);
        navigate(ApplicationPathName.SignIn.Url, { replace: true });
        return;
      }

      // 通常の認証チェック
      const webStorageAuthInfo = getAuthInfoForWebStorage();
      if (webStorageAuthInfo !== null) {
        dispatch(setAuthInfo({
          email: webStorageAuthInfo.email,
          userId: webStorageAuthInfo.sub!,
          userName: webStorageAuthInfo['cognito:username']
        }));
      } else if (isAuthenticated() === false) {
        if (location.pathname !== ApplicationPathName.SignIn.Url) {
          sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, location.pathname + location.search);
        }
        navigate(ApplicationPathName.SignIn.Url, { replace: true });
      }
      setIsAuthReady(true);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 認証ログ出力（ユーザ操作履歴登録）
  // isAuthReady が true になってから実行することで、init() 完了前（idToken 未保存）に
  // putUserHistory が呼ばれてエラーになるのを防ぐ
  useEffect(() => {
    if (isAuthLogSent || !userName || !isAuthReady) {
      return;
    }
    putUserHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    isAuthLogSent = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName, isAuthReady]);

  /**
   * ユーザ操作履歴登録（認証ログ）
   */
  const putUserHistory = async () => {
    try {
      await WebAPI.requestBackground({
        query: `mutation putUserHistory($input: UserHistoryInput) {
          putUserHistory(input: $input) {
            status
            message
          }
        }`,
        variables: {
          input: {
            insertEmployeeId: email,
            crud: "U",
            resourceId: "00",
            query: "auth"
          }
        }
      });
    } catch (error) {
      console.error('putUserHistory:', error);
    }
  };

  // 画面遷移時のイベントログ出力
  useEffect(() => {
    if (currentPath !== location.pathname) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      currentPath = location.pathname;
      // ログインしていなければイベントログを出力できないため
      // 認証済みの場合に限定する
      if (isAuthenticated()) {
        WebAPI.requestBackground({
          query: `
            mutation {
              logEvent(path: ${JSON.stringify(location.pathname)}) {
                status
              }
            }`,
        }).catch((err) => {
          console.error(err);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleCloseModal = () => {
    dispatch(closeMessage({}));
  }

  const { loading } = useContext(WebAPIContext);

  const getMessageTitile = () => {
    switch (type) {
      case 'info' as MessageType:
        return t('common.message.modalTitle.info');
      case 'warn' as MessageType:
        return t('common.message.modalTitle.warn');
      case 'error' as MessageType:
        return t('common.message.modalTitle.error');
      default:
        return ''
    }
  }

  const getMessageClass = () => {
    switch (type) {
      case 'info' as MessageType:
        return 'mm-sf-message-info';
      case 'warn' as MessageType:
        return 'mm-sf-message-warn';
      case 'error' as MessageType:
        return 'mm-sf-message-error';
      default:
        return ''
    }
  }

  return (
    <>
      <Routes>
        {/* FAQ画面 */}
        <Route path={ApplicationPathName.Faq.Url} element={<Faq />} />
        {/* ログイン画面 */}
        <Route path={ApplicationPathName.SignIn.Url} element={<SignIn />} />
        {/* それ以外 */}
        <Route path="*" element={
          isAuthReady ? (
            <>
              <AppHeader />
              <Menu />
              <AppContent />
            </>
          ) : (
            <Box h="100vh" bg="gray.600" display="flex" alignItems="center" justifyContent="center">
              <CircularProgress isIndeterminate color='blue.300' size='80px' />
            </Box>
          )
        } />
      </Routes >
      <Modal isOpen={isOpenModal} onClose={handleCloseModal} closeOnOverlayClick={false} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className={getMessageClass()}>
          <ModalHeader>{getMessageTitile()}</ModalHeader>
          <ModalBody>
            <Box>{message}</Box>
            {detail === '' ? (<></>) : (
              <>
                <Box px="16px" mt="2rem">{t('common.message.modalTitle.detail')}</Box>
                <Box p="16px" m="16px" mt="8px" border="1px solid #eee" bgColor="blackAlpha.100" borderRadius="8px">{detail}</Box>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme='blue' mr={3} onClick={handleCloseModal}>{t('common.button.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Fade in={loading}>
        {loading ? (
          <Box
            w='100vw'
            h='100vh'
            pos='fixed'
            top={0}
            right={0}
            zIndex={9999}
            display='flex'
            alignItems='center'
            justifyContent='center'
            textAlign='center'
            bg='blackAlpha.600'
          >
            <CircularProgress isIndeterminate color='blue.300' size='80px' />
          </Box>
        ) : (<></>)}
      </Fade>
    </>
  );
};

export default App;
